import config from "../config/config";
import axios from "../util/axios";

// Enum for invite status
export enum InviteStatus {
  PENDING = "PENDING",
  ACCEPTED = "ACCEPTED",
  REJECTED = "REJECTED",
}

// Type for a single invite
export interface UserMembershipInvite {
  id: string;
  created_at: string;
  updated_at: string;
  full_name: string;
  email: string;
  referral: string | null;
  status: InviteStatus;
  auth_code: string;
}

// Response types
export interface ListInvitesResponse {
  message: string;
  error: boolean;
  results: UserMembershipInvite[];
}

export interface GetInviteResponse {
  message: string;
  error: boolean;
  results: UserMembershipInvite;
}

export interface UpdateInviteResponse {
  message: string;
  error: boolean;
  results: UserMembershipInvite;
}

export interface DeleteInviteResponse {
  message: string;
  error: boolean;
}

// Request types
export interface UpdateInviteRequest {
  full_name?: string;
  email?: string;
  referral?: string;
  status?: InviteStatus;
}

/**
 * Get all membership invites
 */
export async function getAllInvites(): Promise<ListInvitesResponse> {
  const response = await axios.get(
    `${config.defaults.api_url}/user-membership-invites`,
  );
  return response.data;
}

/**
 * Get a single invite by ID
 * @param id - The invite ID
 */
export async function getInviteById(id: string): Promise<GetInviteResponse> {
  const response = await axios.get(
    `${config.defaults.api_url}/user-membership-invites/${id}`,
  );
  return response.data;
}

/**
 * Update an invite
 * @param id - The invite ID
 * @param data - The update data
 */
export async function updateInvite(
  id: string,
  data: UpdateInviteRequest,
): Promise<UpdateInviteResponse> {
  const response = await axios.patch(
    `${config.defaults.api_url}/user-membership-invites/${id}`,
    data,
  );
  return response.data;
}

/**
 * Accept an invite (shorthand for updating status to ACCEPTED)
 * @param id - The invite ID
 */
export async function acceptInvite(id: string): Promise<UpdateInviteResponse> {
  return updateInvite(id, { status: InviteStatus.ACCEPTED });
}

/**
 * Reject an invite (shorthand for updating status to REJECTED)
 * @param id - The invite ID
 */
export async function rejectInvite(id: string): Promise<UpdateInviteResponse> {
  return updateInvite(id, { status: InviteStatus.REJECTED });
}

/**
 * Delete an invite
 * @param id - The invite ID
 */
export async function deleteInvite(id: string): Promise<DeleteInviteResponse> {
  const response = await axios.delete(
    `${config.defaults.api_url}/user-membership-invites/${id}`,
  );
  return response.data;
}
